import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranscricao } from '../../context/TranscricaoContext';
import useParticipantsWithHost from '../../hooks/useParticipantsWithHost';
import useWSMessageSender from '../../hooks/useWSMessageSender';
import useWSTurnState, { TURN_PHASES } from '../../hooks/useWSTurnState';
import ParticipantCamera from '../Slides/ParticipantCamera';
import './PhraseComponentsCommon.css';
import './WhWordsSection.css';

/**
 * Componente SVG text com outline usando stroke
 */
const WhWordSVG = ({ word, fill = 'white' }) => {
  const textRef = useRef(null);
  const [bbox, setBbox] = useState({ width: 100, height: 42 });

  useEffect(() => {
    if (textRef.current) {
      const textBbox = textRef.current.getBBox();
      setBbox({
        width: Math.max(textBbox.width + 20, 100), // padding extra para o outline
        height: Math.max(textBbox.height + 10, 42),
      });
    }
  }, [word]);

  return (
    <svg
      className="wh-word-svg"
      width={bbox.width}
      height={bbox.height}
      style={{ overflow: 'visible' }}
    >
      <text
        ref={textRef}
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fill={fill}
        style={{
          fontSize: '36px',
          fontWeight: '1000',
          fontFamily: 'Paytone One, Arial, sans-serif',
        }}
      >
        {word}
      </text>
    </svg>
  );
};

/**
 * Componente para interrogação flutuante com apenas contorno (stroke)
 * Desabilitado - ver renderização comentada em WhWordsSection abaixo
 */
// const FloatingQuestionSVG = ({ delay }) => (
//   <svg className="floating-question" style={{ '--i': delay }}>
//     <text
//       x="50%"
//       y="50%"
//       dominantBaseline="central"
//       textAnchor="middle"
//       fill="none"
//       stroke="rgba(255, 255, 255, 0.2)"
//       strokeWidth="3"
//       strokeLinejoin="round"
//       strokeLinecap="round"
//       style={{
//         fontSize: '72px',
//         fontWeight: 'bold',
//         fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
//       }}
//     >
//       ?
//     </text>
//   </svg>
// );

/**
 * Componente para a seção de palavras WH (who, what, where, when, why)
 * @param {Object} props
 * @param {Object} props.participant - Participante da seção
 * @param {Array} props.words - Array de palavras WH (opcional, padrão: ['who', 'what', 'where', 'when', 'why'])
 * @param {string} props.className - Classe CSS adicional
 */
export const WhWordsSection = ({ participant, words = ['who', 'what', 'where', 'when', 'why'], className = '' }) => {
  const { transcriptionHistory } = useTranscricao();
  const { sendTurnAction } = useWSMessageSender();
  const { whoAmI } = useParticipantsWithHost();
  const { currentPhase, turnElapsedSeconds } = useWSTurnState();
  const hasSentQuestion = useRef(false);
  const MAX_ASKING_SECONDS = 15;

  // Guard: só processa se estiver no turno de pergunta correto
  const isMyTurn =
    (currentPhase === TURN_PHASES.AQuestion && whoAmI === 'A') ||
    (currentPhase === TURN_PHASES.BQuestion && whoAmI === 'B');

  // Extrai a última transcrição (compatível com strings e objetos)
  const lastTranscriptionObj = transcriptionHistory.length > 0
    ? transcriptionHistory.at(-1)
    : null;

  const lastTranscription = useMemo(() => {
    if (!lastTranscriptionObj) return '';
    if (typeof lastTranscriptionObj === 'string') return lastTranscriptionObj;
    return lastTranscriptionObj.text || '';
  }, [lastTranscriptionObj]);

  const lastTranscriptionLower = useMemo(
    () => (typeof lastTranscription === 'string' ? lastTranscription.toLowerCase() : ''),
    [lastTranscription]
  );

  const triggerAskedAction = useCallback((transcription) => {
    if (hasSentQuestion.current) return;

    const action = whoAmI === 'A' ? 'A_ASKED' : 'B_ASKED';
    const actionData = {
      action,
      transcription: transcription || '',
    };

    sendTurnAction(actionData);
    hasSentQuestion.current = true;
  }, [sendTurnAction, whoAmI]);

  // Reset do flag quando muda de turno
  useEffect(() => {
    if (!isMyTurn) {
      hasSentQuestion.current = false;
    }
  }, [isMyTurn]);

  // Verificar se fez uma pergunta WH e enviar ação quando necessário
  useEffect(() => {
    if (!isMyTurn) {
      return;
    }

    if (hasSentQuestion.current) {
      return; // Já enviou neste turno
    }

    if (!lastTranscription) {
      return;
    }

    // Verifica se contém alguma wh-word
    const hasWhWord = words.some((w) => lastTranscriptionLower.includes(w));

    // Verifica se termina com ? ou .
    const hasPunctuation = /[?.]\s*$/.test(lastTranscription);

    if (hasWhWord && hasPunctuation) {
      triggerAskedAction(lastTranscriptionLower);
    }
  }, [isMyTurn, lastTranscription, lastTranscriptionLower, triggerAskedAction, words]);

  // Timeout automático caso não haja pergunta no tempo limite
  useEffect(() => {
    if (!isMyTurn) return;
    if (hasSentQuestion.current) return;
    if (turnElapsedSeconds < MAX_ASKING_SECONDS) return;

    triggerAskedAction(lastTranscriptionLower);
  }, [isMyTurn, turnElapsedSeconds, triggerAskedAction, lastTranscriptionLower]);

  // Animação de pulinho quando entra em A_COMPLETING ou B_COMPLETING
  const shouldJump = currentPhase === TURN_PHASES.ACompleting || currentPhase === TURN_PHASES.BCompleting;

  return (
    <div className={`phrase-section-base phrase-section-wh ${className}`}>
      {/* Interrogações flutuantes desabilitadas
      <div className="floating-icons">
        <FloatingQuestionSVG delay="1" />
        <FloatingQuestionSVG delay="2" />
        <FloatingQuestionSVG delay="3" />
        <FloatingQuestionSVG delay="4" />
      </div>
      */}
      {isMyTurn && (
        <span style={{
          position: 'absolute',
          top: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '1.5rem',
          fontWeight: '600',
          color: 'rgba(255, 255, 255, 0.3)',
          textAlign: 'center',
          fontStyle: 'italic',
          pointerEvents: 'none',
          zIndex: 1
        }}>
          {lastTranscription}
        </span>
      )}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', width: '100%', gap: 30 }}>
        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 30 }}>
          {words.slice(0, 3).map((word, index) => (
            /* Teste visual: mesmo container, cantos arredondados e texto na cor do fundo */
            <div
              key={`${word}-${index}`}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '4px 12px',
                display: 'inline-flex',
                boxShadow: '-7px 7px 0 rgba(0, 0, 0, 0.3)',
              }}
            >
              <WhWordSVG word={word} fill="#FF5757" />
            </div>
          ))}
        </div>
        <motion.div
          animate={
            shouldJump
              ? { y: [0, 5, 0] }
              : { y: 0 }
          }
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <ParticipantCamera participant={participant} />
        </motion.div>
      </div>
    </div>
  );
};
